import { createIndustryApi, updateIndustryApi } from "@/api/services/admin/learning-content";
import { Form } from "@/components/elements/Form";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { BasicInfoForm } from "@/features/learning-content/components/IndustryForm/BasicInfoForm";
import { ExamInfoForm } from "@/features/learning-content/components/IndustryForm/ExamInfoForm";
import { ExamRequirementsForm } from "@/features/learning-content/components/IndustryForm/ExamRequirementsForm";
import { industrySchema } from "@/features/learning-content/schema/IndustrySchema";
import { TIndustryForm } from "@/features/learning-content/types/industryForm";
import { convertIndustryFormToCreateReq, convertIndustryFormToUpdateReq } from "@/features/learning-content/utils/converts";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes } from "@/routes/config";
import { useToast } from "@/hooks/useToast";
import { useDialog } from "@/hooks/useDialog";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDynamicRoute } from "@/utils/url";
import { EIndustryExamType } from "@/types/enum";

type TIndustryFormProps = {
  previousData?: Partial<TIndustryForm>;
};

const initFormData: TIndustryForm = {
  name: "",
  isActive: false,
  activationDate: null,
  internalMemo: null,
  examType: EIndustryExamType.CBT,
  examOrganization: null,
  examInfoReleaseEstimate: null,
  examInfoUrl: null,
  nextExamDate: null,
  displayOrder: 0,
  examRequirements: [
    { name: null, description: null },
    { name: null, description: null },
    { name: null, description: null },
  ],
};

export const IndustryForm = forwardRef<{ submit: () => void }, TIndustryFormProps>(({ previousData }, ref) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const confirmDeleteChecked = useDialog();
  const isEditMode = !!id;
  const initialValues = useMemo(() => ({ ...initFormData, ...previousData }), [previousData]);

  const methods = useForm<TIndustryForm, object, TIndustryForm>({
    defaultValues: initialValues,
    resolver: yupResolver(industrySchema) as never,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const watchedName = methods.watch("name");

  const onSubmit = async (data: TIndustryForm) => {
    // 受験要件: a requirement is "deleted" when its 項目名 is cleared. If that requirement already has
    // learner checks (hasCheckedLearners), confirm before submitting; skip the confirm otherwise.
    if (isEditMode && id) {
      const isDeletingCheckedRequirement = data.examRequirements?.some((req) => req.hasCheckedLearners && !req.name?.trim());
      if (isDeletingCheckedRequirement) {
        const confirmed = await confirmDeleteChecked.show();
        if (!confirmed) return;
      }
    }

    setLoading(true);
    try {
      if (isEditMode && id) {
        const payload = convertIndustryFormToUpdateReq(data);
        const rs = await updateIndustryApi(id, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_LIST] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
        navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
      } else {
        const payload = convertIndustryFormToCreateReq(data);
        const rs = await createIndustryApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_LIST] });
        navigate(AppRoutes.learningContent);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={industrySchema} initialValues={initialValues}>
      <MenuNavigate
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              {watchedName || "(分野名未入力)"}
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "基本情報",
            content: <BasicInfoForm />,
          },
          {
            title: "試験基本情報",
            content: (
              <Stack gap={3}>
                <ExamInfoForm />
                <ExamRequirementsForm />
              </Stack>
            ),
          },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button
                size="sm-round"
                variant="secondary"
                onClick={() => {
                  if (isEditMode && id) {
                    navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
                  } else {
                    navigate(AppRoutes.learningContent);
                  }
                }}
              >
                戻る
              </Button>
              <Stack direction="row" gap={3}>
                <Button disabled={loading} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />

      {/* 受験要件: confirm when an existing requirement with learner checks is being deleted on save */}
      <ConfirmSaveModal open={confirmDeleteChecked.open} onOk={confirmDeleteChecked.confirm} onClose={confirmDeleteChecked.cancel}>
        <Typography component="p" variant="subtitle1">
          要件を削除すると、すでにお客様が入力されているチェックは削除されますがよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </Form>
  );
});
